import React, { useState, useEffect, useRef } from 'react';
import { PortableText } from '@portabletext/react';
import { formatPublishDate } from '@jwp/ott-hooks-react/src/cms/utils/dateUtils';
import { useUrlNavigation } from '@jwp/ott-hooks-react/src/useUrlNavigation';
import type { Insight } from '@jwp/ott-common/types/insights';

import { SharePopover } from '../SharePopover/SharePopover';
import { portableTextComponents } from '../../../PortableTextComponent/PortableTextComponent';

import styles from './InsightDetail.module.scss';

interface InsightDetailProps {
  insight: Insight;
  onBack: () => void;
}

export const InsightDetail: React.FC<InsightDetailProps> = ({ insight, onBack }) => {
  const [showSharePopover, setShowSharePopover] = useState(false);
  const { getCurrentInsightURL } = useUrlNavigation();
  const shareContainerRef = useRef<HTMLDivElement>(null);

  const captionText = insight.thumbnailSource || '';
  const formattedDate = formatPublishDate(insight.publishDate);

  const handleShareClick = (): void => {
    setShowSharePopover(!showSharePopover);
  };

  const handleCloseSharePopover = (): void => {
    setShowSharePopover(false);
  };

  // Close popover when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      const target = event.target as Element;
      if (showSharePopover && !target.closest('[data-share-container]')) {
        setShowSharePopover(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showSharePopover]);

  return (
    <article className={styles.detailView}>
      <button type="button" className={styles.backButton} onClick={onBack} aria-label="Back to Articles">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M19 12H6" />
          <path d="M12 19l-7-7 7-7" />
        </svg>
        Back to Articles
      </button>

      <h1 className={styles.detailTitle}>{insight.title}</h1>

      {insight.image && (
        <figure className={styles.detailFigure}>
          <img src={insight.image} alt={insight.title} className={styles.detailImage} />
          {captionText && <figcaption className={styles.detailCaption}>{captionText}</figcaption>}
        </figure>
      )}

      <div className={styles.detailBar}>
        <div className={styles.detailByline}>
          <div className={styles.detailSource}>{insight.author}</div>
          <div className={styles.detailTime}>{formattedDate}</div>
        </div>
        <div className={styles.shareContainer} ref={shareContainerRef} data-share-container>
          <button type="button" className={styles.detailShare} aria-label="Share article" onClick={handleShareClick}>
            <svg width="24" height="25" viewBox="0 0 24 25" fill="none">
              <path
                d="M8.59 14.01L15.42 17.99M15.41 7.01L8.59 10.99M21 5.5C21 7.15685 19.6569 8.5 18 8.5C16.3431 8.5 15 7.15685 15 5.5C15 3.84315 16.3431 2.5 18 2.5C19.6569 2.5 21 3.84315 21 5.5ZM9 12.5C9 14.1569 7.65685 15.5 6 15.5C4.34315 15.5 3 14.1569 3 12.5C3 10.8431 4.34315 9.5 6 9.5C7.65685 9.5 9 10.8431 9 12.5ZM21 19.5C21 21.1569 19.6569 22.5 18 22.5C16.3431 22.5 15 21.1569 15 19.5C15 17.8431 16.3431 16.5 18 16.5C19.6569 16.5 21 17.8431 21 19.5Z"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <div className={styles.detailShareCount}>Share</div>
          </button>
          <SharePopover isVisible={showSharePopover} currentURL={getCurrentInsightURL()} onClose={handleCloseSharePopover} />
        </div>
      </div>

      <h2 className={styles.detailSubTitle}>{insight.title}</h2>

      <div className={styles.detailContent}>
        <PortableText value={insight.fullContent} components={portableTextComponents} />
      </div>
    </article>
  );
};
